import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, ListChecks } from 'lucide-react';
import { HudHint, HudHintStatus } from '../../services/hud';
import {
  HINT_KINDS,
  HINT_STATUS_STYLE,
  KIND_STYLE,
  KnownKind,
  formatTime,
  normaliseKind,
  tabHref,
  toMs,
} from './hudShared';

type StatusFilter = 'all' | HudHintStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Open' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'expired', label: 'Expired' },
];

function HintRow({ hint, search }: { hint: HudHint; search: string }) {
  const [open, setOpen] = useState(false);
  const kind = KIND_STYLE[normaliseKind(hint.kind)];
  const status = HINT_STATUS_STYLE[hint.status] ?? HINT_STATUS_STYLE.active;
  const hasDetail = !!hint.detail && hint.detail.trim() !== '' && hint.detail.trim() !== hint.text.trim();
  const faded = hint.status === 'expired' || hint.status === 'ignored';
  return (
    <div className={`px-5 py-3 flex flex-col gap-1.5 ${faded ? 'opacity-70' : ''}`}>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => hasDetail && setOpen(o => !o)}
          className={`mt-0.5 shrink-0 ${hasDetail ? 'text-slate-400 hover:text-navy' : 'text-transparent cursor-default'}`}
          aria-label={open ? 'Hide detail' : 'Show detail'}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border shrink-0 ${kind.badge}`}>
          {kind.label}
        </span>
        {hint.priority === 'high' && (
          <span className="flex items-center gap-0.5 text-[10px] font-bold uppercase text-red-600 shrink-0" title="High priority">
            <AlertTriangle size={12} /> High
          </span>
        )}
        <p className="flex-1 text-sm text-navy leading-snug">{hint.text}</p>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${status.pill}`}>{status.label}</span>
      </div>
      {open && hasDetail && (
        <p className="ml-7 text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">{hint.detail}</p>
      )}
      <p className="ml-7 text-xs text-slate-400 flex flex-wrap items-center gap-x-1">
        <span>
          Raised {formatTime(hint.raisedAt)}
          {hint.closedAt && `, closed ${formatTime(hint.closedAt)}`}
          {hint.by && ` by ${hint.by}`}
        </span>
        {hint.status === 'confirmed' && hint.adrId && (
          <Link
            to={tabHref(search, 'requirements', { reqTab: 'adrs' })}
            title={`Open the session's ADRs (${hint.adrId})`}
            className="ml-2 inline-flex items-center gap-1 font-semibold text-primary hover:underline"
          >
            <FileText size={12} /> Became ADR
          </Link>
        )}
      </p>
    </div>
  );
}

function rate(good: number, closed: number): string {
  return closed ? `${Math.round((good / closed) * 100)}%` : '-';
}

function SummaryBar({ hints }: { hints: HudHint[] }) {
  const stats = useMemo(() => {
    const byKind = new Map<KnownKind, { good: number; closed: number }>();
    let good = 0, closed = 0, resolved = 0, expired = 0, open = 0;
    for (const h of hints) {
      if (h.status === 'active') { open += 1; continue; }
      if (h.status === 'resolved') { resolved += 1; continue; }
      if (h.status === 'expired') { expired += 1; continue; }
      const k = normaliseKind(h.kind);
      const entry = byKind.get(k) ?? { good: 0, closed: 0 };
      entry.closed += 1;
      closed += 1;
      if (h.status === 'accepted' || h.status === 'confirmed') { entry.good += 1; good += 1; }
      byKind.set(k, entry);
    }
    const kinds = HINT_KINDS.filter(k => byKind.has(k)).map(k => ({ kind: k, ...byKind.get(k)! }));
    return { good, closed, resolved, expired, open, kinds };
  }, [hints]);

  return (
    <div className="flex items-center gap-3 text-xs overflow-x-auto whitespace-nowrap">
      <span
        className="font-bold text-navy"
        title={`${stats.good} accepted or confirmed out of ${stats.closed} answered (resolved and expired excluded)`}
      >
        Accept rate {rate(stats.good, stats.closed)}
        <span className="font-normal text-slate-400"> ({stats.good}/{stats.closed})</span>
      </span>
      {stats.kinds.length > 0 && <span className="h-3 w-px bg-slate-200" />}
      {stats.kinds.map(k => (
        <span key={k.kind} className="text-slate-500" title={`${k.good} of ${k.closed}`}>
          {KIND_STYLE[k.kind].label} <span className="font-semibold text-navy">{rate(k.good, k.closed)}</span>
        </span>
      ))}
      <span className="h-3 w-px bg-slate-200" />
      <span className="text-slate-500">{stats.open} open</span>
      <span className="text-slate-500">{stats.resolved} covered by the room</span>
      <span className="text-slate-500">{stats.expired} expired</span>
    </div>
  );
}

function hourLabel(t: number): string {
  const start = new Date(t);
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 3_600_000);
  const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${fmt(start)} to ${fmt(end)}`;
}

export function HudTimeline({ hints }: { hints: HudHint[] }) {
  const location = useLocation();
  const [kinds, setKinds] = useState<Set<KnownKind>>(new Set());
  const [status, setStatus] = useState<StatusFilter>('all');

  const sorted = useMemo(
    () => [...hints].sort((a, b) => toMs(b.raisedAt) - toMs(a.raisedAt)),
    [hints],
  );
  const presentKinds = useMemo(
    () => HINT_KINDS.filter(k => hints.some(h => normaliseKind(h.kind) === k)),
    [hints],
  );

  const groups = useMemo(() => {
    const filtered = sorted.filter(h =>
      (kinds.size === 0 || kinds.has(normaliseKind(h.kind))) && (status === 'all' || h.status === status));
    const out: { key: string; label: string; hints: HudHint[] }[] = [];
    for (const h of filtered) {
      const t = toMs(h.raisedAt);
      const key = t ? String(Math.floor(t / 3_600_000)) : 'unknown';
      let g = out[out.length - 1];
      if (!g || g.key !== key) {
        g = { key, label: t ? hourLabel(t) : 'Unknown time', hints: [] };
        out.push(g);
      }
      g.hints.push(h);
    }
    return out;
  }, [sorted, kinds, status]);

  const toggleKind = (k: KnownKind) => setKinds(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const shownCount = groups.reduce((n, g) => n + g.hints.length, 0);

  return (
    <section className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-[#e2e8f0] flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="text-primary" />
          <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Hint timeline</h2>
          <span className="ml-auto text-xs text-slate-400">
            {shownCount === hints.length ? `${hints.length} total` : `${shownCount} of ${hints.length}`}
          </span>
        </div>
        <SummaryBar hints={hints} />
        {hints.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1.5">
              {presentKinds.map(k => {
                const on = kinds.has(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleKind(k)}
                    aria-pressed={on}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded border transition-colors ${
                      on ? KIND_STYLE[k].badge + ' ring-1 ring-current' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {KIND_STYLE[k].label}
                  </button>
                );
              })}
              {kinds.size > 0 && (
                <button type="button" onClick={() => setKinds(new Set())} className="text-[11px] font-semibold text-primary hover:underline px-1">
                  Clear
                </button>
              )}
            </div>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-full">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatus(f.value)}
                  className={`flex-1 py-1.5 text-xs font-semibold transition-all ${
                    status === f.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {hints.length === 0 ? (
        <p className="p-10 text-center text-sm text-slate-400">No hints raised yet.</p>
      ) : groups.length === 0 ? (
        <p className="p-10 text-center text-sm text-slate-400">No hints match these filters.</p>
      ) : (
        groups.map(g => (
          <div key={g.key}>
            <div className="px-5 py-1.5 bg-slate-50/80 border-b border-slate-100 flex items-center text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              {g.label}
              <span className="ml-auto font-semibold normal-case tracking-normal text-slate-400">
                {g.hints.length} {g.hints.length === 1 ? 'hint' : 'hints'}
              </span>
            </div>
            <div className="divide-y divide-slate-100 border-b border-slate-100">
              {g.hints.map(h => <HintRow key={h.hintId} hint={h} search={location.search} />)}
            </div>
          </div>
        ))
      )}
    </section>
  );
}
